import { getAppVersion } from '@/utils/general';

const RELOADED_VERSION_KEY = 'weave_chunk_reload_version';

let backgroundLoads = 0;

/**
 * Starts loading a chunk nobody is waiting for yet. If it fails, the page is not reloaded: the user did not ask for
 * anything, and the chunk is loaded again, with the usual reload on failure, when it is actually needed.
 */
export const loadInBackground = (load: () => Promise<unknown>) => {
  backgroundLoads++;
  void load()
    .catch(() => undefined)
    .finally(() => backgroundLoads--);
};

/**
 * Reloads the page when a lazy-loaded chunk fails to load. Each deploy replaces the hashed files under `/assets`, so a
 * tab opened before the deploy cannot fetch the chunks of a route it has not visited yet. Reloading picks up the new
 * `index.html`. It reloads at most once per deployed version, so a chunk that keeps failing is left to the error
 * boundary instead of reloading forever, however long each attempt takes.
 */
const init = () => {
  window.addEventListener('vite:preloadError', (event) => {
    // Vite raises this event inside the failing import's own promise chain, before the caller's `catch`. The event
    // doesn't say which import failed, so a route load failing while a background load is pending is not reloaded
    // either and reaches the error boundary. Tagging each import would lift that.
    if (backgroundLoads > 0) return;
    const version = String(getAppVersion());
    if (sessionStorage.getItem(RELOADED_VERSION_KEY) === version) return;
    sessionStorage.setItem(RELOADED_VERSION_KEY, version);
    event.preventDefault();
    window.location.reload();
  });
};

export default init;
